// src/components/modals/GeneratorModal.tsx
import React, { useState, useEffect } from 'react';

interface GeneratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedAgent?: string | null;
    fetchTestCases: () => void;
    isGenerating: boolean;
    setIsGenerating: (generating: boolean) => void;
    mode?: 'scenarios' | 'documents';
    onDataRefresh?: () => void;
}

interface Document {
    id: string;
    filename: string;
    file_type: string;
    file_size: number;
    upload_date: string;
    processing_status: string;
}

interface TemplateDefinition {
    id: string;
    name: string;
    category: string;
    description: string;
    fields: TemplateField[];
    export_formats: string[];
    is_system: boolean;
}

interface TemplateField {
    key: string;
    label: string;
    type: string;
    required: boolean;
    description?: string;
    options?: string[];
}

interface FieldSelection {
    [fieldKey: string]: boolean;
}

type GeneratorTab = 'documents' | 'requirements' | 'testcases' | 'templates' | 'execute';

// Function for orchestrator API
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};

// Function for document intelligence API
const getDocsApiBaseUrl = () => {
    if (process.env.REACT_APP_DOCS_API_BASE_URL) {
        return process.env.REACT_APP_DOCS_API_BASE_URL;
    }
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8013";
    }
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8013";
    }
    const apiHost = hostname.replace('frontend-alpha', 'document-intelligence');
    return `https://${apiHost}`;
};

const API_BASE_URL = getApiBaseUrl();
const DOCS_API_BASE_URL = getDocsApiBaseUrl();

// DocumentsTab component with simplified document management
const DocumentsTab: React.FC = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [previewModal, setPreviewModal] = useState<{ isOpen: boolean, content: string }>({ isOpen: false, content: '' });

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = async () => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/list`);
            if (response.ok) {
                const docs = await response.json();
                setDocuments(docs);
            }
        } catch (error) {
            console.error('Failed to load documents:', error);
        }
    };

    const handleFileUpload = async (files: FileList) => {
        if (files.length === 0) return;

        setIsUploading(true);
        const formData = new FormData();

        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/upload`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                await loadDocuments();

                const duplicates = result.filter((r: any) => r.processing_status === 'duplicate');
                if (duplicates.length > 0) {
                    alert(`${duplicates.length} duplicate document(s) detected and skipped. Showing existing versions.`);
                }
            } else {
                const error = await response.json();
                alert(`Upload failed: ${error.detail}`);
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleProcessDocument = async (documentId: string) => {
        try {
            setIsUploading(true);
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/${documentId}/process`, {
                method: 'POST'
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    alert(`✅ Document processed successfully!`);
                    await loadDocuments();
                } else {
                    alert(`❌ Processing failed: ${result.error || 'Unknown error'}`);
                }
            } else {
                const error = await response.json();
                alert(`❌ Processing failed: ${error.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Process error:', error);
            alert('❌ Error processing document. Check if the service is running.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteDocument = async (documentId: string, filename: string) => {
        if (!window.confirm(`Are you sure to delete "${filename}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/${documentId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                alert(`Document "${filename}" deleted successfully.`);
                await loadDocuments();
            } else {
                const error = await response.json();
                alert(`Delete failed: ${error.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('Error deleting document. Check if the service is running.');
        }
    };

    const handlePreviewDocument = async (documentId: string) => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/${documentId}/analysis`);

            if (response.ok) {
                const analysis = await response.json();
                const sections = analysis.sections || [];
                const businessElements = analysis.analysis_summary?.business_elements_found || {};

                const previewContent = `
DOCUMENT ANALYSIS RESULTS

Basic Info:
- File: ${analysis.document.filename}
- Type: ${analysis.analysis_summary.content_type}
- Size: ${(analysis.document.file_size / 1024).toFixed(1)} KB
- Processing Time: ${analysis.analysis_summary.processing_time}s
- Quality: ${analysis.analysis_summary.extraction_quality}

Structure Analysis:
- Total Sections: ${analysis.analysis_summary.total_sections}

Detected Sections:
${sections.map((section: any, index: number) =>
                    `${index + 1}. ${section.section_title}
  └ ${section.word_count} words (confidence: ${(section.confidence_score * 100).toFixed(1)}%)`
                ).join('\n')}

Business Elements Found:
${Object.entries(businessElements).map(([category, items]: [string, any]) => {
                    const count = Array.isArray(items) ? items.length : 0;
                    return `• ${category.charAt(0).toUpperCase() + category.slice(1)}: ${count} detected`;
                }).join('\n')}

Recommendation:
${analysis.analysis_summary.content_type === 'business_requirements' ?
                        'Ready for Business Requirements Generation!' :
                        'Good for general processing and analysis'}
           `.trim();

                setPreviewModal({ isOpen: true, content: previewContent });

            } else {
                alert('Failed to load document analysis. Please try again.');
            }
        } catch (error) {
            console.error('Preview error:', error);
            alert('Error loading document preview. Check if the service is running.');
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        handleFileUpload(files);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFileUpload(e.target.files);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'processed': return '#28a745';
            case 'processing': return '#ffc107';
            case 'uploaded': return '#6c757d';
            case 'failed': return '#dc3545';
            default: return '#6c757d';
        }
    };

    return (
        <div className="tab-content">
            <h3>Document Management</h3>

            <div
                style={{
                    padding: '2rem',
                    border: dragOver ? '2px dashed #007bff' : '2px dashed #ddd',
                    borderRadius: '8px',
                    marginBottom: '2rem',
                    textAlign: 'center',
                    backgroundColor: dragOver ? '#f8f9ff' : '#fff'
                }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
            >
                <div style={{ color: '#666' }}>
                    <p>Drag & drop files here or click to upload</p>
                    <p style={{ fontSize: '0.9rem' }}>• Multi-file upload (2MB per file)</p>
                    <p style={{ fontSize: '0.9rem' }}>• Supported: PDF, DOCX, TXT, MD, JSON, YAML</p>
                    <p style={{ fontSize: '0.9rem' }}>• Duplicates detected automatically</p>

                    <input
                        type="file"
                        multiple
                        accept=".pdf,.docx,.txt,.md,.json,.yaml,.yml"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="file-upload"
                        disabled={isUploading}
                    />

                    <label htmlFor="file-upload">
                        <button
                            className="widget-button button-primary"
                            style={{ marginTop: '1rem', opacity: isUploading ? 0.6 : 1 }}
                            disabled={isUploading}
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('file-upload')?.click();
                            }}
                        >
                            {isUploading ? 'Uploading...' : 'Select Files'}
                        </button>
                    </label>
                </div>
            </div>

            {documents.length > 0 && (
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #dee2e6'
                }}>
                    <button
                        className="widget-button button-primary"
                        onClick={() => {
                            documents.forEach(doc => {
                                if (doc.processing_status === 'uploaded') {
                                    handleProcessDocument(doc.id);
                                }
                            });
                        }}
                        disabled={isUploading || documents.filter(d => d.processing_status === 'uploaded').length === 0}
                    >
                        ⚡ Process All ({documents.filter(d => d.processing_status === 'uploaded').length})
                    </button>
                    <span style={{
                        fontSize: '0.8rem',
                        color: '#666',
                        alignSelf: 'center'
                    }}>
                        Process all uploaded documents for AI analysis
                    </span>
                </div>
            )}

            <div>
                <h4>Uploaded Documents ({documents.length})</h4>

                {documents.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        <p>No documents uploaded yet. Upload some files to get started!</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
                        {documents.map((doc, index) => (
                            <div
                                key={doc.id || index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    backgroundColor: '#f9f9f9'
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontSize: '1.2rem' }}>
                                            {doc.file_type === 'pdf' ? '📄' :
                                                doc.file_type === 'docx' ? '📝' :
                                                    doc.file_type === 'txt' ? '📃' :
                                                        doc.file_type === 'md' ? '📋' : '📄'}
                                        </span>
                                        <strong>{doc.filename || doc.original_filename}</strong>
                                        <span
                                            style={{
                                                fontSize: '0.8rem',
                                                padding: '0.2rem 0.5rem',
                                                backgroundColor: getStatusColor(doc.processing_status),
                                                color: 'white',
                                                borderRadius: '12px'
                                            }}
                                        >
                                            {doc.processing_status}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                        {formatFileSize(doc.file_size)} • {doc.file_type.toUpperCase()} • Uploaded {formatDate(doc.upload_date)}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        className="widget-button button-secondary"
                                        style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                                        onClick={() => handleProcessDocument(doc.id)}
                                        disabled={isUploading || doc.processing_status === 'processing'}
                                    >
                                        {doc.processing_status === 'uploaded' ? 'Process' : 'Reprocess'}
                                    </button>
                                    <button
                                        className="widget-button button-secondary"
                                        style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                                        onClick={() => handlePreviewDocument(doc.id)}
                                        disabled={doc.processing_status !== 'processed' && doc.processing_status !== 'uploaded'}
                                    >
                                        Preview
                                    </button>
                                    <button
                                        className="widget-button"
                                        style={{
                                            fontSize: '0.8rem',
                                            padding: '0.4rem 0.8rem',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: '1px solid #dc3545'
                                        }}
                                        onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                                    >
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {previewModal.isOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setPreviewModal({ isOpen: false, content: '' })}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '2rem',
                            borderRadius: '8px',
                            maxWidth: '80vw',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer'
                            }}
                            onClick={() => setPreviewModal({ isOpen: false, content: '' })}
                        >
                            ×
                        </button>
                        <pre style={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            lineHeight: '1.4',
                            margin: 0,
                            paddingRight: '2rem'
                        }}>
                            {previewModal.content}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};

// RequirementsTab component
const RequirementsTab: React.FC = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [selectedDoc, setSelectedDoc] = useState<string>('');
    const [selectedTemplate, setSelectedTemplate] = useState<string>('agile_user_stories');
    const [templateDefinitions, setTemplateDefinitions] = useState<TemplateDefinition[]>([]);
    const [currentTemplate, setCurrentTemplate] = useState<TemplateDefinition | null>(null);
    const [fieldSelection, setFieldSelection] = useState<FieldSelection>({});
    const [requirements, setRequirements] = useState<any[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewRequirements, setPreviewRequirements] = useState<any[]>([]);
    const [selectedRequirement, setSelectedRequirement] = useState<any>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    useEffect(() => {
        loadDocuments();
        loadTemplateDefinitions();
    }, []);

    useEffect(() => {
        if (selectedTemplate) {
            loadTemplateDefinition(selectedTemplate);
        }
    }, [selectedTemplate]);

    const loadDocuments = async () => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/list`);
            if (response.ok) {
                const docs = await response.json();
                const processedDocs = docs.filter((doc: any) => doc.processing_status === 'uploaded' || doc.processing_status === 'processed');
                setDocuments(processedDocs);
            }
        } catch (error) {
            console.error('Failed to load documents:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadTemplateDefinitions = async () => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/templates/definitions/list`);
            if (response.ok) {
                const data = await response.json();
                const businessTemplates = data.templates.filter((t: any) => t.category === 'business_requirements');
                setTemplateDefinitions(businessTemplates);
            }
        } catch (error) {
            console.error('Failed to load template definitions:', error);
        }
    };

    const loadTemplateDefinition = async (templateId: string) => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/templates/definitions/${templateId}`);
            if (response.ok) {
                const template = await response.json();
                setCurrentTemplate(template);

                const initialSelection: FieldSelection = {};
                template.fields.forEach((field: TemplateField) => {
                    initialSelection[field.key] = field.required;
                });
                setFieldSelection(initialSelection);

                loadSavedFieldSelection(templateId);
            }
        } catch (error) {
            console.error('Failed to load template definition:', error);
        }
    };

    const loadSavedFieldSelection = async (templateId: string) => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/templates/field-selection/${templateId}`);
            if (response.ok) {
                const saved = await response.json();
                if (saved.selected_fields && saved.selected_fields.length > 0) {
                    const selection: FieldSelection = {};
                    saved.selected_fields.forEach((fieldKey: string) => {
                        selection[fieldKey] = true;
                    });
                    setFieldSelection(selection);
                }
            }
        } catch (error) {
            console.error('Failed to load saved field selection:', error);
        }
    };

    const saveFieldSelection = async () => {
        if (!selectedTemplate) return;

        const selectedFields = Object.keys(fieldSelection).filter(key => fieldSelection[key]);

        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/templates/field-selection/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    template_id: selectedTemplate,
                    selected_fields: selectedFields
                })
            });

            if (response.ok) {
                console.log('Field selection saved');
            }
        } catch (error) {
            console.error('Failed to save field selection:', error);
        }
    };

    const loadDocumentAnalysis = async (docId: string) => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/documents/${docId}/analysis`);
            if (response.ok) {
                const analysis = await response.json();

                let existingReqs = [];
                if (analysis.document?.metadata?.ai_analysis?.requirements_generation?.requirements) {
                    existingReqs = analysis.document.metadata.ai_analysis.requirements_generation.requirements;
                }

                setRequirements(existingReqs);
            }
        } catch (error) {
            console.error('Failed to load document analysis:', error);
        }
    };

    const handleDocumentChange = async (docId: string) => {
        setSelectedDoc(docId);
        setRequirements([]);
        if (docId) {
            await loadDocumentAnalysis(docId);
        }
    };

    const handleFieldSelectionChange = (fieldKey: string, checked: boolean) => {
        const newSelection = { ...fieldSelection, [fieldKey]: checked };
        setFieldSelection(newSelection);
        saveFieldSelection();
    };

    const handleGenerate = async () => {
        if (!selectedDoc || !selectedTemplate) return;

        const selectedFields = Object.keys(fieldSelection).filter(key => fieldSelection[key]);
        if (selectedFields.length === 0) {
            alert('Please select at least one field to generate requirements.');
            return;
        }

        setIsGenerating(true);
        try {
            const resp = await fetch(`${DOCS_API_BASE_URL}/documents/${selectedDoc}/generate-requirements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    template_name: selectedTemplate
                })
            });

            const result = await resp.json();

            if (resp.ok && result?.success) {
                await loadDocumentAnalysis(selectedDoc);
                alert(`Generated ${result.requirements_generation?.requirements_count || 0} requirements with ${selectedFields.length} fields`);
            } else {
                alert(`Generation failed: ${result?.error || 'Unknown error'}`);
            }
        } catch (e) {
            console.error('Generate error', e);
            alert('Error generating requirements');
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePreviewRequirements = () => {
        setPreviewRequirements(requirements);
        setShowPreviewModal(true);
    };

    const handleRequirementDetail = (requirement: any) => {
        setSelectedRequirement(requirement);
        setShowDetailModal(true);
    };

    const exportRequirements = async (format: 'excel' | 'pdf' | 'json') => {
        if (requirements.length === 0) {
            alert('No requirements to export. Generate requirements first.');
            return;
        }

        const selectedFields = Object.keys(fieldSelection).filter(key => fieldSelection[key]);
        if (selectedFields.length === 0) {
            alert('No fields selected for export. Please select fields in the template section.');
            return;
        }

        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/export/requirements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    template_id: selectedTemplate,
                    selected_fields: selectedFields,
                    requirements: requirements,
                    export_format: format
                })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const fileExtension = format === 'excel' ? 'csv' : format;
                a.download = `requirements_${selectedTemplate}_${new Date().toISOString().split('T')[0]}.${fileExtension}`;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);

                alert(`Export completed successfully! (${selectedFields.length} fields included)`);
            } else {
                const error = await response.json();
                alert(`Export failed: ${error.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Export error:', error);
            alert(`Error exporting to ${format.toUpperCase()}. Please try again.`);
        }
    };

    const getSelectedFieldsCount = () => {
        return Object.values(fieldSelection).filter(Boolean).length;
    };

    const getRequiredFieldsCount = () => {
        if (!currentTemplate) return 0;
        return currentTemplate.fields.filter(f => f.required).length;
    };

    return (
        <div className="tab-content">
            <h3>Business Requirements Generation</h3>

            {isLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
            ) : (
                <>
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '6px' }}>
                        <h4>1. Select Document</h4>
                        <select
                            value={selectedDoc}
                            onChange={(e) => handleDocumentChange(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', margin: '0.5rem 0' }}
                        >
                            <option value="">Choose a document...</option>
                            {documents.map(doc => (
                                <option key={doc.id} value={doc.id}>
                                    {doc.filename} ({doc.file_type.toUpperCase()}) - {new Date(doc.upload_date).toLocaleDateString()}
                                </option>
                            ))}
                        </select>
                        {documents.length === 0 && (
                            <p style={{ color: '#666', fontSize: '0.9rem' }}>No documents available. Upload documents in the Documents tab first.</p>
                        )}
                    </div>

                    {selectedDoc && (
                        <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '6px' }}>
                            <h4>2. Select Template & Configure Fields</h4>

                            <div style={{ marginBottom: '1rem' }}>
                                <label><strong>Template:</strong></label>
                                <select
                                    value={selectedTemplate}
                                    onChange={(e) => setSelectedTemplate(e.target.value)}
                                    style={{ width: '100%', padding: '0.5rem', margin: '0.5rem 0' }}
                                >
                                    {templateDefinitions.map(template => (
                                        <option key={template.id} value={template.id}>
                                            {template.name} - {template.description}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {currentTemplate && (
                                <div style={{ marginTop: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h5 style={{ margin: 0 }}>Select Fields to Include:</h5>
                                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                            {getSelectedFieldsCount()} selected ({getRequiredFieldsCount()} required)
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                        gap: '0.75rem',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        padding: '0.5rem',
                                        border: '1px solid #eee',
                                        borderRadius: '4px',
                                        backgroundColor: '#f9f9f9'
                                    }}>
                                        {currentTemplate.fields.map((field) => (
                                            <div key={field.key} style={{
                                                padding: '0.75rem',
                                                backgroundColor: 'white',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd'
                                            }}>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '0.5rem',
                                                    cursor: 'pointer'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={fieldSelection[field.key] || false}
                                                        onChange={(e) => handleFieldSelectionChange(field.key, e.target.checked)}
                                                        disabled={field.required}
                                                        style={{ marginTop: '0.2rem' }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            fontWeight: 'bold',
                                                            color: field.required ? '#dc3545' : '#333'
                                                        }}>
                                                            {field.label}
                                                            {field.required && <span style={{ color: '#dc3545' }}> *</span>}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.8rem',
                                                            color: '#666',
                                                            marginTop: '0.25rem'
                                                        }}>
                                                            {field.description}
                                                        </div>
                                                        {field.type === 'select' && field.options && (
                                                            <div style={{
                                                                fontSize: '0.75rem',
                                                                color: '#888',
                                                                marginTop: '0.25rem'
                                                            }}>
                                                                Options: {field.options.join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            className="widget-button button-secondary"
                                            onClick={() => {
                                                const allSelected: FieldSelection = {};
                                                currentTemplate.fields.forEach(field => {
                                                    allSelected[field.key] = true;
                                                });
                                                setFieldSelection(allSelected);
                                                saveFieldSelection();
                                            }}
                                            style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            className="widget-button button-secondary"
                                            onClick={() => {
                                                const requiredOnly: FieldSelection = {};
                                                currentTemplate.fields.forEach(field => {
                                                    requiredOnly[field.key] = field.required;
                                                });
                                                setFieldSelection(requiredOnly);
                                                saveFieldSelection();
                                            }}
                                            style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                                        >
                                            Required Only
                                        </button>
                                        <button
                                            className="widget-button button-secondary"
                                            onClick={() => {
                                                const noneSelected: FieldSelection = {};
                                                currentTemplate.fields.forEach(field => {
                                                    noneSelected[field.key] = field.required;
                                                });
                                                setFieldSelection(noneSelected);
                                                saveFieldSelection();
                                            }}
                                            style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                                        >
                                            Clear Optional
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: '1rem' }}>
                                <button
                                    className="pushable push-primary btn-sm"
                                    onClick={handleGenerate}
                                    disabled={!selectedDoc || !selectedTemplate || isGenerating || getSelectedFieldsCount() === 0}
                                >
                                    <span className="shadow"></span>
                                    <span className="edge"></span>
                                    <span className="front">
                                        {isGenerating ? 'Generating…' : `Generate Requirements (${getSelectedFieldsCount()} fields)`}
                                    </span>
                                </button>
                                <button
                                    className="pushable push-primary btn-sm"
                                    onClick={() => selectedDoc && loadDocumentAnalysis(selectedDoc)}
                                    disabled={!selectedDoc || isGenerating}
                                >
                                    <span className="shadow"></span>
                                    <span className="edge"></span>
                                    <span className="front">Refresh Analysis</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {requirements.length > 0 && (
                        <div style={{ marginTop: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={handlePreviewRequirements}
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Preview</span>
                                    </button>

                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => exportRequirements('json')}
                                        disabled={getSelectedFieldsCount() === 0}
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">📄 JSON ({getSelectedFieldsCount()} fields)</span>
                                    </button>
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => exportRequirements('excel')}
                                        disabled={getSelectedFieldsCount() === 0}
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">📊 Excel ({getSelectedFieldsCount()} fields)</span>
                                    </button>
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => exportRequirements('pdf')}
                                        disabled={getSelectedFieldsCount() === 0}
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">📋 PDF ({getSelectedFieldsCount()} fields)</span>
                                    </button>
                                </div>
                            </div>

                            <h4>Generated Business Requirements ({requirements.length})</h4>

                            {currentTemplate && getSelectedFieldsCount() > 0 && (
                                <div style={{
                                    marginBottom: '1rem',
                                    padding: '0.75rem',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '4px',
                                    border: '1px solid #bbdefb'
                                }}>
                                    <strong>Selected Fields ({getSelectedFieldsCount()}):</strong>{' '}
                                    {Object.keys(fieldSelection)
                                        .filter(key => fieldSelection[key])
                                        .map(key => {
                                            const field = currentTemplate.fields.find(f => f.key === key);
                                            return field ? field.label : key;
                                        })
                                        .join(', ')}
                                </div>
                            )}

                            <div style={{ border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Title</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Priority</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Stakeholders</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requirements.map((req, index) => (
                                            <tr key={req.id || index} style={{ borderBottom: index < requirements.length - 1 ? '1px solid #eee' : 'none' }}>
                                                <td style={{ padding: '0.75rem' }}>{req.id}</td>
                                                <td style={{ padding: '0.75rem' }}><strong>{req.title}</strong></td>
                                                <td style={{ padding: '0.75rem' }}>{req.priority}</td>
                                                <td style={{ padding: '0.75rem' }}>{req.type}</td>
                                                <td style={{ padding: '0.75rem' }}>{req.stakeholders?.join(', ') || 'None'}</td>
                                                <td style={{ padding: '0.75rem' }}>
                                                    <button
                                                        className="widget-button button-secondary"
                                                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                                                        onClick={() => handleRequirementDetail(req)}
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {selectedDoc && requirements.length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                            <p>No requirements found for this document. Select fields and click "Generate Requirements" to create them.</p>
                        </div>
                    )}
                </>
            )}

            {showPreviewModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowPreviewModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '2rem',
                            borderRadius: '8px',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            position: 'relative',
                            minWidth: '800px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3>Requirements Preview ({previewRequirements.length} items)</h3>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    className="widget-button button-primary"
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        exportRequirements('json');
                                    }}
                                    disabled={getSelectedFieldsCount() === 0}
                                >
                                    📄 Export JSON
                                </button>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowPreviewModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gap: '1.5rem' }}>
                            {previewRequirements.map((req, index) => (
                                <div key={req.id || index} style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: '1.5rem',
                                    backgroundColor: '#f9f9f9'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                        <div>
                                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#007bff' }}>{req.id}: {req.title}</h4>
                                            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#666' }}>
                                                <span><strong>Priority:</strong> {req.priority}</span>
                                                <span><strong>Type:</strong> {req.type}</span>
                                                <span><strong>Effort:</strong> {req.estimated_effort || 'TBD'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {req.description && (
                                        <div style={{ marginBottom: '1rem' }}>
                                            <strong>Description:</strong>
                                            <p style={{ margin: '0.5rem 0', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                                                {req.description}
                                            </p>
                                        </div>
                                    )}

                                    {req.acceptance_criteria && req.acceptance_criteria.length > 0 && (
                                        <div style={{ marginBottom: '1rem' }}>
                                            <strong>Acceptance Criteria:</strong>
                                            <ul style={{ margin: '0.5rem 0', padding: '0.5rem 0 0.5rem 1.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                                                {req.acceptance_criteria.map((criteria: string, idx: number) => (
                                                    <li key={idx} style={{ marginBottom: '0.25rem' }}>{criteria}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                                        <div>
                                            <strong>Stakeholders:</strong>
                                            <div style={{ margin: '0.25rem 0' }}>
                                                {req.stakeholders && req.stakeholders.length > 0 ? req.stakeholders.join(', ') : 'None specified'}
                                            </div>
                                        </div>
                                        <div>
                                            <strong>Dependencies:</strong>
                                            <div style={{ margin: '0.25rem 0' }}>
                                                {req.dependencies && req.dependencies.length > 0 ? req.dependencies.join(', ') : 'None'}
                                            </div>
                                        </div>
                                    </div>

                                    {req.compliance_tags && req.compliance_tags.length > 0 && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <strong>Compliance Tags:</strong>
                                            <div style={{ margin: '0.25rem 0' }}>
                                                {req.compliance_tags.map((tag: string, idx: number) => (
                                                    <span key={idx} style={{
                                                        display: 'inline-block',
                                                        backgroundColor: '#007bff',
                                                        color: 'white',
                                                        padding: '0.2rem 0.5rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        marginRight: '0.5rem'
                                                    }}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showDetailModal && selectedRequirement && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1001
                    }}
                    onClick={() => setShowDetailModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '2rem',
                            borderRadius: '8px',
                            maxWidth: '600px',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer'
                            }}
                            onClick={() => setShowDetailModal(false)}
                        >
                            ×
                        </button>

                        <h3 style={{ color: '#007bff', marginBottom: '1rem' }}>
                            {selectedRequirement.id}: {selectedRequirement.title}
                        </h3>

                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div><strong>Priority:</strong> {selectedRequirement.priority}</div>
                                <div><strong>Type:</strong> {selectedRequirement.type}</div>
                            </div>

                            {selectedRequirement.description && (
                                <div>
                                    <strong>Description:</strong>
                                    <p style={{ margin: '0.5rem 0', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                        {selectedRequirement.description}
                                    </p>
                                </div>
                            )}

                            {selectedRequirement.acceptance_criteria && selectedRequirement.acceptance_criteria.length > 0 && (
                                <div>
                                    <strong>Acceptance Criteria:</strong>
                                    <ul style={{ margin: '0.5rem 0', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                        {selectedRequirement.acceptance_criteria.map((criteria: string, idx: number) => (
                                            <li key={idx} style={{ marginBottom: '0.5rem' }}>{criteria}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div>
                                <strong>Stakeholders:</strong>
                                <div style={{ margin: '0.5rem 0' }}>
                                    {selectedRequirement.stakeholders && selectedRequirement.stakeholders.length > 0
                                        ? selectedRequirement.stakeholders.join(', ')
                                        : 'None specified'}
                                </div>
                            </div>

                            <div>
                                <strong>Dependencies:</strong>
                                <div style={{ margin: '0.5rem 0' }}>
                                    {selectedRequirement.dependencies && selectedRequirement.dependencies.length > 0
                                        ? selectedRequirement.dependencies.join(', ')
                                        : 'None'}
                                </div>
                            </div>

                            <div>
                                <strong>Estimated Effort:</strong>
                                <div style={{ margin: '0.5rem 0' }}>{selectedRequirement.estimated_effort || 'To be determined'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// TemplatesTab component
const TemplatesTab: React.FC = () => {
    const [templateDefinitions, setTemplateDefinitions] = useState<TemplateDefinition[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateDefinition | null>(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);

    useEffect(() => {
        loadTemplateDefinitions();
    }, []);

    const loadTemplateDefinitions = async () => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/templates/definitions/list`);
            if (response.ok) {
                const data = await response.json();
                setTemplateDefinitions(data.templates || []);
            }
        } catch (error) {
            console.error('Failed to load template definitions:', error);
        }
    };

    const viewTemplateDetails = (template: TemplateDefinition) => {
        setSelectedTemplate(template);
        setShowTemplateModal(true);
    };

    return (
        <div className="tab-content">
            <h3>Template Definitions</h3>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
                Manage field-based templates for generating business requirements, test cases, and other artifacts.
            </p>

            <div style={{ display: 'grid', gap: '2rem' }}>
                {['business_requirements', 'test_cases', 'api_requirements'].map(category => {
                    const categoryTemplates = templateDefinitions.filter(t => t.category === category);
                    const categoryName = category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

                    return (
                        <div key={category}>
                            <h4>{categoryName} ({categoryTemplates.length})</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem' }}>
                                {categoryTemplates.map(template => (
                                    <div key={template.id} style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '1.5rem',
                                        backgroundColor: '#f9f9f9'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                            <div>
                                                <h5 style={{ margin: '0 0 0.5rem 0', color: '#007bff' }}>{template.name}</h5>
                                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                                    {template.fields.length} fields • {template.export_formats.join(', ')} export
                                                </div>
                                            </div>
                                            {template.is_system && (
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    padding: '0.2rem 0.5rem',
                                                    backgroundColor: '#28a745',
                                                    color: 'white',
                                                    borderRadius: '8px'
                                                }}>
                                                    SYSTEM
                                                </span>
                                            )}
                                        </div>

                                        <p style={{ fontSize: '0.9rem', margin: '0 0 1rem 0', lineHeight: '1.4' }}>
                                            {template.description}
                                        </p>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <strong style={{ fontSize: '0.8rem', color: '#666' }}>REQUIRED FIELDS:</strong>
                                            <div style={{ fontSize: '0.8rem', color: '#333', marginTop: '0.25rem' }}>
                                                {template.fields
                                                    .filter(f => f.required)
                                                    .map(f => f.label)
                                                    .join(', ') || 'None'}
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <strong style={{ fontSize: '0.8rem', color: '#666' }}>OPTIONAL FIELDS:</strong>
                                            <div style={{ fontSize: '0.8rem', color: '#333', marginTop: '0.25rem' }}>
                                                {template.fields
                                                    .filter(f => !f.required)
                                                    .map(f => f.label)
                                                    .slice(0, 3)
                                                    .join(', ')}
                                                {template.fields.filter(f => !f.required).length > 3 &&
                                                    ` +${template.fields.filter(f => !f.required).length - 3} more`}
                                            </div>
                                        </div>

                                        <button
                                            className="widget-button button-primary"
                                            style={{ width: '100%' }}
                                            onClick={() => viewTemplateDetails(template)}
                                        >
                                            View All Fields ({template.fields.length})
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {showTemplateModal && selectedTemplate && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowTemplateModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '2rem',
                            borderRadius: '8px',
                            maxWidth: '80vw',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            position: 'relative',
                            minWidth: '600px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer'
                            }}
                            onClick={() => setShowTemplateModal(false)}
                        >
                            ×
                        </button>

                        <h3 style={{ color: '#007bff', marginBottom: '1rem' }}>
                            {selectedTemplate.name}
                        </h3>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <p style={{ margin: '0 0 1rem 0', color: '#666' }}>{selectedTemplate.description}</p>
                            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem' }}>
                                <span><strong>Category:</strong> {selectedTemplate.category.replace('_', ' ')}</span>
                                <span><strong>Fields:</strong> {selectedTemplate.fields.length}</span>
                                <span><strong>Export Formats:</strong> {selectedTemplate.export_formats.join(', ')}</span>
                            </div>
                        </div>

                        <h4>Field Definitions ({selectedTemplate.fields.length})</h4>
                        <div style={{ display: 'grid', gap: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
                            {selectedTemplate.fields.map((field) => (
                                <div key={field.key} style={{
                                    padding: '1rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    backgroundColor: field.required ? '#fff3cd' : '#f8f9fa'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                        <h5 style={{ margin: 0, color: field.required ? '#856404' : '#007bff' }}>
                                            {field.label}
                                            {field.required && <span style={{ color: '#dc3545' }}> *</span>}
                                        </h5>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <span style={{
                                                fontSize: '0.7rem',
                                                padding: '0.2rem 0.5rem',
                                                backgroundColor: '#007bff',
                                                color: 'white',
                                                borderRadius: '8px'
                                            }}>
                                                {field.type.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                                        <strong>Key:</strong> {field.key}
                                    </div>

                                    {field.description && (
                                        <div style={{ fontSize: '0.9rem', margin: '0.5rem 0' }}>
                                            {field.description}
                                        </div>
                                    )}

                                    {field.type === 'select' && field.options && (
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                            <strong>Options:</strong> {field.options.join(', ')}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// TestCasesTab component with API support
const TestCasesTab: React.FC<{
    selectedAgent: string | null;
    fetchTestCases: () => void;
    isGenerating: boolean;
    setIsGenerating: (generating: boolean) => void;
    onDataRefresh?: () => void;
}> = ({ selectedAgent, fetchTestCases, isGenerating, setIsGenerating, onDataRefresh }) => {

    // State variables
    const [exportTemplate, setExportTemplate] = useState<TemplateDefinition | null>(null);
    const [exportFieldSelection, setExportFieldSelection] = useState<FieldSelection>({});
    const [generationType, setGenerationType] = useState<'agent' | 'requirements' | 'integrated'>('agent');
    const [availableRequirements, setAvailableRequirements] = useState<any[]>([]);
    const [selectedRequirements, setSelectedRequirements] = useState<string[]>([]);
    const [testCaseTemplate, setTestCaseTemplate] = useState<TemplateDefinition | null>(null);
    const [fieldSelection, setFieldSelection] = useState<FieldSelection>({});
    const [generatedTestCases, setGeneratedTestCases] = useState<any[]>([]);
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');

    // Enhanced agent support state
    const [agentContext, setAgentContext] = useState<any>(null);
    const [isEnhancedAgent, setIsEnhancedAgent] = useState(false);
    const [availableTestTypes, setAvailableTestTypes] = useState<string[]>(['Functional']);
    const [selectedTestTypes, setSelectedTestTypes] = useState<string[]>(['Functional']);
    const [hybridMode, setHybridMode] = useState(false);
    const [isTestCasesExpanded, setIsTestCasesExpanded] = useState(false);
    const [testCounts, setTestCounts] = useState<{ [key: string]: number }>({});
    const [countInputs, setCountInputs] = useState<{ [key: string]: string }>({});


    // API support state (Step 13A, 13B, 13C)
    const [availableAPIs, setAvailableAPIs] = useState<any[]>([]);
    const [entityType, setEntityType] = useState<'agent' | 'api'>('agent');
    const [selectedAPI, setSelectedAPI] = useState<string>('');
    const [agents, setAgents] = useState<any[]>([]);

    // Load existing test cases when selectedAgent changes
    useEffect(() => {
        if (selectedAgent && entityType === 'agent') {
            loadExistingTestCases();
            loadAgentContext();
        } else if (selectedAPI && entityType === 'api') {
            loadExistingTestCases();
            loadAPIContext();
        }
    }, [selectedAgent, selectedAPI, entityType]);

    // Load agents and APIs on mount
    useEffect(() => {
        fetchAvailableAPIs();
        fetchAvailableAgents();
    }, []);

    const fetchAvailableAgents = async () => {
        try {
            //const response = await fetch(`${API_BASE_URL}/agents`);
            const response = await fetch(`${API_BASE_URL}/agent-manager/agents/intelligent`);
            if (response.ok) {
                const data = await response.json();
                setAgents(data.agents || []);
            }
        } catch (error) {
            console.error('Failed to fetch agents:', error);
        }
    };

    const fetchAvailableAPIs = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/endpoints`);
            if (response.ok) {
                const data = await response.json();
                //console.log('[GeneratorModal] API fetch response:', data);

                // Filter out any remaining platform integrations that might slip through
                const PLATFORM_DOMAINS = ['jira', 'confluence', 'database', 'databricks',
                    'bitbucket', 'github', 'gitlab', 'slack', 'teams'];

                const applicationAPIs = (data.endpoints || []).filter((api: any) => {
                    // Exclude if it's a platform domain
                    if (PLATFORM_DOMAINS.includes(api.domain)) {
                        return false;
                    }
                    // Exclude if marked as platform integration in enhanced context
                    if (api.enhanced_context?.is_platform_integration) {
                        return false;
                    }
                    return true;
                });

                // FIX: Set the filtered APIs array, not the enhanced statuses object
                setAvailableAPIs(applicationAPIs);
                //console.log('[GeneratorModal] Set APIs:', applicationAPIs);
            }
        } catch (error) {
            console.error('[GeneratorModal] Failed to fetch APIs:', error);
            setAvailableAPIs([]);
        }
    };


    const loadExistingTestCases = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/db/test-cases`);
            if (response.ok) {
                const allTestCases = await response.json();
                // Filter test cases based on current entity
                const filteredTestCases = allTestCases.filter((tc: any) => {
                    if (entityType === 'agent') {
                        return tc.agent_name === selectedAgent && tc.entity_type === 'agent';
                    } else if (entityType === 'api') {
                        return tc.agent_name === selectedAPI && tc.entity_type === 'api';
                    }
                    return false;
                });
                setGeneratedTestCases(filteredTestCases);
            } else {
                setGeneratedTestCases([]);
            }
        } catch (error) {
            console.error('Failed to load existing test cases:', error);
            setGeneratedTestCases([]);
        }
    };

    const loadExistingTestCasesOld = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/db/test-cases`);
            if (response.ok) {
                const allTestCases = await response.json();
                const agentTestCases = allTestCases.filter((tc: any) => tc.agent_name === selectedAgent);
                setGeneratedTestCases(agentTestCases);
            } else {
                setGeneratedTestCases([]);
            }
        } catch (error) {
            console.error('Failed to load existing test cases:', error);
            setGeneratedTestCases([]);
        }
    };

    const loadAgentContext = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/generator/agents/${selectedAgent}/context`);
            if (response.ok) {
                const context = await response.json();
                setAgentContext(context);

                const enhanced = context.enhanced_mode || context.enhanced_context || context.domain_analysis;
                setIsEnhancedAgent(enhanced);

                if (enhanced) {
                    const domain = context.enhanced_context?.selected_domain || context.domain_analysis?.suggested_domain;
                    setAvailableTestTypes(getTestTypesForDomain(domain));
                    setSelectedTestTypes(['Functional', 'Reliability']);
                    setHybridMode(true);
                }
            }
        } catch (error) {
            console.error('Failed to load agent context:', error);
            setIsEnhancedAgent(false);
            setHybridMode(false);
        }
    };

    const loadAPIContext = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/endpoints/${selectedAPI}/context`);
            if (response.ok) {
                const context = await response.json();
                setAgentContext(context);

                // Check for enhanced API context - FIX THIS LOGIC
                const enhanced = !!(context.enhanced_context || context.domain_analysis);  // Add !! to ensure boolean
                setIsEnhancedAgent(enhanced);

                // Add debug logging
                //console.log('[GeneratorModal] API Context loaded:', context);
                //console.log('[GeneratorModal] Is Enhanced API:', enhanced);

                if (enhanced) {
                    const domain = context.enhanced_context?.selected_domain || context.domain;
                    setAvailableTestTypes(getTestTypesForDomain(domain));
                    setSelectedTestTypes(['Functional', 'Integration']);
                    setHybridMode(true);
                }
            }
        } catch (error) {
            console.error('Failed to load API context:', error);
            setIsEnhancedAgent(false);
            setHybridMode(false);
        }
    };

    const getTestTypesForDomain = (domain: string) => {
        const baseTypes = ['Functional', 'Reliability', 'Guardrail', 'Edge Cases'];

        switch (domain) {
            case 'telecommunications':
                return [...baseTypes, 'GDPR Compliance', 'Network Integration'];
            case 'ecommerce':
                return [...baseTypes, 'Payment Security', 'Order Processing'];
            case 'finance':
                return [...baseTypes, 'Regulatory Compliance', 'Fraud Detection'];
            case 'healthcare':
                return [...baseTypes, 'HIPAA Compliance', 'Patient Privacy'];
            default:
                return baseTypes;
        }
    };

    useEffect(() => {
        const loadExportTemplate = async () => {
            try {
                const response = await fetch(`${DOCS_API_BASE_URL}/templates/definitions/test_cases_template`);
                if (response.ok) {
                    const template = await response.json();
                    setExportTemplate(template);

                    const initialSelection: FieldSelection = {};
                    if (template && template.fields) {
                        template.fields.forEach((field: TemplateField) => {
                            initialSelection[field.key] = true;
                        });
                    }
                    setExportFieldSelection(initialSelection);
                }
            } catch (error) {
                console.error('Failed to load test case export template:', error);
            }
        };

        loadExportTemplate();
    }, []);

    useEffect(() => {
        loadTestCaseTemplate();
        if (generationType !== 'agent') {
            loadAvailableRequirements();
        }
    }, [generationType]);

    const loadTestCaseTemplate = async () => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/templates/definitions/test_cases_template`);
            if (response.ok) {
                const template = await response.json();
                setTestCaseTemplate(template);

                const initialSelection: FieldSelection = {};
                template.fields.forEach((field: TemplateField) => {
                    initialSelection[field.key] = field.required;
                });
                setFieldSelection(initialSelection);
            }
        } catch (error) {
            console.error('Failed to load test case template:', error);
        }
    };

    const loadAvailableRequirements = async () => {
        try {
            const response = await fetch(`${DOCS_API_BASE_URL}/requirements`);
            if (response.ok) {
                const requirements = await response.json();
                setAvailableRequirements(requirements);
            }
        } catch (error) {
            console.error('Failed to load requirements:', error);
        }
    };

    const generateTestCases = async () => {
        const targetEntity = entityType === 'agent' ? selectedAgent : selectedAPI;

        if (!targetEntity) {
            alert(`Please select ${entityType === 'agent' ? 'an agent' : 'an API'} first`);
            return;
        }

        setIsGenerating(true);
        setProgress(0);
        setStatusMessage('Initializing test generation...');
        //console.log(`[GeneratorModal] Starting generation for ${entityType}:`, targetEntity);

        try {
            const testsPerCategory: { [key: string]: number } = {};
            selectedTestTypes.forEach(type => {
                testsPerCategory[type] = testCounts[type] || 3;
            });

            const requestData: any = {
                test_categories: selectedTestTypes,
                tests_per_category: testsPerCategory,
                hybrid_mode: hybridMode,
                entity_type: entityType
            };

            // Add entity-specific fields
            //if (entityType === 'agent') {
            //    requestData.agent_name = selectedAgent;
            //} else {
            //    requestData.agent_name = selectedAPI;
            //    requestData.api_name = selectedAPI;
            //    requestData.api_id = availableAPIs.find(api => api.name === selectedAPI)?.id;
            //    requestData.entity_type = 'api';
            //}

            if (entityType === 'agent') {
                requestData.agent_name = selectedAgent;
            } else {
                // For API, don't set agent_name - use api_name only
                requestData.api_name = selectedAPI;
                requestData.api_id = availableAPIs.find(api => api.name === selectedAPI)?.id;
                // entity_type is already set above, no need to set again
            }

            // Add enhanced context if available
            if (isEnhancedAgent && agentContext) {
                requestData.selected_domain = agentContext.enhanced_context?.selected_domain;
                requestData.selected_templates = agentContext.selected_templates;

                //requestData.agent_code_analysis = agentContext.capabilities || {};

                {
                    const caps = agentContext.capabilities;
                    if (typeof caps === 'string') {
                        try { requestData.agent_code_analysis = caps.trim() ? JSON.parse(caps) : {}; }
                        catch { requestData.agent_code_analysis = {}; }
                    } else {
                        requestData.agent_code_analysis = caps || {};
                    }
                }

                requestData.custom_documents = agentContext.enhanced_context?.custom_documents || [];
                requestData.user_text_prompt = agentContext.enhanced_context?.user_text_prompt;
                requestData.business_context = agentContext.domain_analysis || {};

                setStatusMessage('Using enhanced domain intelligence...');
                //console.log('[GeneratorModal] Is Enhanced:', isEnhancedAgent);
            }

            // Add requirements if using integrated mode
            if (generationType === 'requirements' || generationType === 'integrated') {
                requestData.requirement_ids = selectedRequirements;
            }

            setProgress(20);
            setStatusMessage(hybridMode ? 'Generating domain-intelligent test cases...' : 'Generating test cases...');

            const response = await fetch(`${API_BASE_URL}/generator/generate-test-cases-async`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                throw new Error(`Generation failed: ${response.status}`);
            }

            const result = await response.json();
            setProgress(50);

            if (result.job_id) {
                await pollGenerationStatus(result.job_id);
            } else {
                throw new Error('No job ID returned');
            }

        } catch (error) {
            console.error('Test generation failed:', error);
            setStatusMessage(`❌ Generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setIsGenerating(false);
        }
    };

    const pollGenerationStatus = async (jobId: string) => {
        const maxAttempts = 60;
        let attempts = 0;

        const poll = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/generator/jobs/${jobId}/status`);
                if (response.ok) {
                    const status = await response.json();

                    setProgress(Math.min(50 + (attempts * 2), 90));
                    setStatusMessage(status.message || 'Processing...');

                    if (status.status && status.status.toUpperCase() === 'COMPLETED') {
                        setProgress(100);
                        setStatusMessage('✅ Test generation completed!');
                        setGeneratedTestCases(status.test_cases || []);

                        setTimeout(() => {
                            fetchTestCases();
                            if (onDataRefresh) {
                                onDataRefresh();
                            }
                        }, 1000);

                        setIsGenerating(false);
                        return;
                    } else if (status.status && status.status.toUpperCase() === 'FAILED') {
                        setStatusMessage(`❌ Generation failed: ${status.error || 'Unknown error'}`);
                        setIsGenerating(false);
                        return;
                    }

                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, 5000);
                    } else {
                        setStatusMessage('❌ Generation timeout - please try again');
                        setIsGenerating(false);
                    }
                } else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, 5000);
                    } else {
                        setStatusMessage('❌ Failed to check status - service may be down');
                        setIsGenerating(false);
                    }
                }
            } catch (error) {
                console.error('Polling error:', error);
                setStatusMessage(`❌ Status check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                setIsGenerating(false);
            }
        };

        poll();
    };

    return (
        <div className="tab-content">
            <h3>Scenarios Generation for: <span style={{ color: '#007bff' }}>
                {entityType === 'agent' ? selectedAgent : selectedAPI || 'No Target Selected'}
            </span></h3>

            {/* Enhanced Entity Information */}
            {isEnhancedAgent && agentContext && (
                <div style={{
                    marginBottom: '2rem',
                    padding: '1rem',
                    backgroundColor: '#e8f5e8',
                    border: '1px solid #4caf50',
                    borderRadius: '6px'
                }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#2e7d32' }}>
                        Enhanced {entityType === 'agent' ? 'Agent' : 'API'} Detected
                    </h4>
                    <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
                        <strong>Domain:</strong> {agentContext.enhanced_context?.selected_domain || agentContext.domain_analysis?.suggested_domain || 'Not specified'}<br />
                        <strong>Custom Documents:</strong> {agentContext.enhanced_context?.custom_documents?.length || 0} uploaded<br />
                        <strong>Custom Instructions:</strong> {agentContext.enhanced_context?.user_text_prompt ? 'Provided' : 'None'}<br />
                        <strong>Capabilities:</strong> {Object.keys(agentContext.capabilities || {}).length} detected
                    </div>
                </div>
            )}

            {/* Step 13D: Entity Selection (Agent or API) */}
            <div style={{ marginBottom: '2rem' }}>
                <h4>Select Target for Test Generation</h4>

                {/* Entity Type Selection */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            value="agent"
                            checked={entityType === 'agent'}
                            onChange={() => {
                                setEntityType('agent');
                                setSelectedAPI('');
                            }}
                        />
                        <span>Agents</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            value="api"
                            checked={entityType === 'api'}
                            onChange={() => {
                                setEntityType('api');
                            }}
                        />
                        <span>API Endpoints</span>
                    </label>

                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        opacity: 0.5,
                        cursor: 'not-allowed'
                    }}
                        title="Application test generation coming soon">
                        <input
                            type="radio"
                            value="application"
                            checked={false}
                            disabled={true}
                            style={{ cursor: 'not-allowed' }}
                        />
                        <span style={{ color: '#999' }}>Applications (Coming Soon)</span>
                    </label>


                </div>

                {/* Entity Dropdown */}
                {entityType === 'agent' ? (
                    <select
                        value={selectedAgent || ''}
                        onChange={(e) => e.target.value}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            fontSize: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white'
                        }}
                        disabled
                    >
                        <option value="">-- Select an Agent --</option>
                        {agents && agents.length > 0 ? (
                            agents.map((agent: any) => (
                                <option key={agent.id || agent.name} value={agent.name}>
                                    {agent.name}
                                    {agent.enhanced_mode && ' ⭐ (Enhanced)'}
                                    {agent.domain && ` - ${agent.domain}`}
                                </option>
                            ))
                        ) : (
                            <option disabled>No agents available</option>
                        )}
                    </select>
                ) : (
                    <select
                        value={selectedAPI}
                        onChange={(e) => setSelectedAPI(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            fontSize: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white'
                        }}
                    >
                        <option value="">-- Select an API Endpoint --</option>
                        {availableAPIs && availableAPIs.length > 0 ? (
                            availableAPIs.map((api: any) => (
                                <option key={api.id} value={api.name}>
                                    {api.name}
                                    {api.domain && ` (${api.domain})`}
                                    {api.base_url && ` - ${api.base_url}`}
                                </option>
                            ))
                        ) : (
                            <option disabled>No API endpoints available</option>
                        )}
                    </select>
                )}

                {/* Show selected entity info */}
                {(selectedAgent || selectedAPI) && (
                    <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: '#f0f8ff',
                        border: '1px solid #b0d4ff',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                    }}>
                        <strong>Selected {entityType === 'agent' ? 'Agent' : 'API'}:</strong> {entityType === 'agent' ? selectedAgent : selectedAPI}
                        {entityType === 'api' && availableAPIs.find(api => api.name === selectedAPI)?.domain && (
                            <span> | Domain: {availableAPIs.find(api => api.name === selectedAPI)?.domain}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Generation Mode Selection */}
            <div style={{ marginBottom: '2rem' }}>
                <h4>Generation Mode</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            value="agent"
                            checked={generationType === 'agent'}
                            onChange={(e) => setGenerationType(e.target.value as any)}
                        />
                        <span>{entityType === 'agent' ? 'Agent' : 'API'}-Based</span>
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>
                            ({isEnhancedAgent ? 'Enhanced with domain intelligence' : 'Standard generation'})
                        </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            value="requirements"
                            checked={generationType === 'requirements'}
                            onChange={(e) => setGenerationType(e.target.value as any)}
                        />
                        <span>Requirements-Based</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            value="integrated"
                            checked={generationType === 'integrated'}
                            onChange={(e) => setGenerationType(e.target.value as any)}
                        />
                        <span>Integrated ({entityType === 'agent' ? 'Agent' : 'API'} + Requirements)</span>
                    </label>
                </div>
            </div>

            {/* Test Type Selection */}
            <div style={{ marginBottom: '2rem' }}>
                <h4>Test Types to Generate</h4>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '0.75rem'
                }}>
                    {availableTestTypes.map(type => (
                        <label key={type} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            backgroundColor: selectedTestTypes.includes(type) ? '#e3f2fd' : '#fff',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={selectedTestTypes.includes(type)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedTestTypes([...selectedTestTypes, type]);
                                        setTestCounts(prev => ({ ...prev, [type]: 3 }));
                                        setCountInputs(prev => ({ ...prev, [type]: '3' }));   // NEW
                                    } else {
                                        setSelectedTestTypes(selectedTestTypes.filter(t => t !== type));
                                        setTestCounts(prev => {
                                            const newCounts = { ...prev };
                                            delete newCounts[type];
                                            return newCounts;
                                        });
                                        setCountInputs(prev => {                              // NEW
                                            const next = { ...prev };
                                            delete next[type];
                                            return next;
                                        });
                                    }
                                }}

                            />
                            <span style={{
                                fontSize: '0.9rem',
                                fontWeight: selectedTestTypes.includes(type) ? 'bold' : 'normal',
                                flex: 1
                            }}>
                                {type}
                            </span>
                            {selectedTestTypes.includes(type) && (
                                <>
                                    <input
                                        // text input to allow empty while editing; still shows numeric keypad
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        list={`count-options-${type}`}              // suggestions 1..50 (datalist below)
                                        value={
                                            countInputs[type] !== undefined
                                                ? countInputs[type]
                                                : String(testCounts[type] ?? 3)
                                        }
                                        onChange={(e) => {
                                            // allow only digits (or empty string) while typing
                                            const v = e.target.value;
                                            if (/^[0-9]*$/.test(v)) {
                                                setCountInputs(prev => ({ ...prev, [type]: v }));
                                            }
                                        }}
                                        onBlur={() => {
                                            // commit to numeric state on blur; keep any positive integer
                                            const raw = countInputs[type];
                                            const num = raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
                                            if (!isNaN(num) && num >= 1) {
                                                setTestCounts(prev => ({ ...prev, [type]: num }));
                                                setCountInputs(prev => ({ ...prev, [type]: String(num) }));
                                            } else {
                                                // revert to last known number (or default 3) if invalid/empty
                                                const fallback = testCounts[type] ?? 3;
                                                setCountInputs(prev => ({ ...prev, [type]: String(fallback) }));
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: '72px',              // fixed width so it stays inside the tile
                                            padding: '0.25rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            textAlign: 'center'
                                        }}
                                        placeholder="1"
                                    />
                                    <datalist id={`count-options-${type}`}>
                                        {Array.from({ length: 50 }, (_, i) => i + 1).map(n => (
                                            <option key={n} value={String(n)} />
                                        ))}
                                    </datalist>
                                </>
                            )}



                        </label>
                    ))}
                </div>
            </div>

            {/* Requirements Selection (for requirements/integrated mode) */}
            {(generationType === 'requirements' || generationType === 'integrated') && (
                <div style={{ marginBottom: '2rem' }}>
                    <h4>Select Requirements</h4>
                    <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '1rem'
                    }}>
                        {availableRequirements.length > 0 ? (
                            availableRequirements.map((req, index) => (
                                <label key={index} style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedRequirements.includes(req.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedRequirements([...selectedRequirements, req.id]);
                                            } else {
                                                setSelectedRequirements(selectedRequirements.filter(id => id !== req.id));
                                            }
                                        }}
                                    />
                                    <span style={{ marginLeft: '0.5rem' }}>{req.title}</span>
                                </label>
                            ))
                        ) : (
                            <p style={{ color: '#666' }}>No requirements available. Upload documents first.</p>
                        )}
                    </div>
                </div>
            )}

            {/* Generation Progress */}
            {isGenerating && (
                <div style={{
                    marginBottom: '2rem',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px'
                }}>
                    <h4 style={{ margin: '0 0 1rem 0' }}>⚙️ Generating Test Cases</h4>
                    <div style={{
                        width: '100%',
                        height: '20px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        marginBottom: '0.5rem'
                    }}>
                        <div style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: '#4caf50',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                        {statusMessage} ({progress}%)
                    </p>
                </div>
            )}

            {/* Generation Results */}
            {generatedTestCases.length > 0 && (
                <div style={{
                    marginBottom: '2rem',
                    padding: '1rem',
                    backgroundColor: '#e8f5e8',
                    border: '1px solid #4caf50',
                    borderRadius: '6px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem'
                    }}>
                        <h4 style={{ margin: 0, color: '#2e7d32' }}>
                            ✅ {generatedTestCases.length} Test Cases for {entityType === 'agent' ? selectedAgent : selectedAPI}
                        </h4>
                        <button
                            onClick={() => setIsTestCasesExpanded(!isTestCasesExpanded)}
                            style={{
                                background: 'none',
                                border: '1px solid #4caf50',
                                color: '#2e7d32',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            {isTestCasesExpanded ? 'Collapse All' : 'Expand All'}
                        </button>
                    </div>
                    <div style={{ maxHeight: isTestCasesExpanded ? 'none' : '300px', overflowY: 'auto' }}>
                        {(isTestCasesExpanded ? generatedTestCases : generatedTestCases.slice(0, 3)).map((testCase, index) => (
                            <div key={index} style={{
                                marginBottom: '1rem',
                                padding: '0.75rem',
                                backgroundColor: '#fff',
                                border: '1px solid #c8e6c9',
                                borderRadius: '4px'
                            }}>
                                <strong>{testCase.test_name || testCase.name || `Test Case ${index + 1}`}</strong>
                                <span style={{
                                    marginLeft: '1rem',
                                    fontSize: '0.8rem',
                                    color: '#666',
                                    backgroundColor: '#f0f0f0',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '3px'
                                }}>
                                    {testCase.test_type || testCase.type || 'Functional'}
                                </span>
                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                                    {testCase.task_prompt || testCase.description}
                                </p>
                            </div>
                        ))}
                        {!isTestCasesExpanded && generatedTestCases.length > 3 && (
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>
                                ... and {generatedTestCases.length - 3} more test cases (click "Expand All" to see them)
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Generate Button */}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button
                    className="widget-button button-primary"
                    onClick={generateTestCases}
                    disabled={(!selectedAgent && !selectedAPI) || isGenerating || selectedTestTypes.length === 0}
                    style={{ minWidth: '200px' }}
                >
                    {isGenerating ? 'Generating...' : (
                        isEnhancedAgent ? 'Generate Enhanced Scenarios' : 'Generate Scenarios'
                    )}
                </button>

                {isEnhancedAgent && (
                    <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
                        Domain-intelligent generation enabled
                    </div>
                )}
            </div>

            {/* Selected Entity Info */}
            {(selectedAgent || selectedAPI) && (
                <div style={{
                    marginTop: '2rem',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                }}>
                    <strong>Selected {entityType === 'agent' ? 'Agent' : 'API'}:</strong> {entityType === 'agent' ? selectedAgent : selectedAPI}<br />
                    <strong>Entity Type:</strong> {entityType}<br />
                    <strong>Generation Mode:</strong> {generationType}<br />
                    <strong>Test Types:</strong> {selectedTestTypes.join(', ')}<br />
                    {isEnhancedAgent && <><strong>Enhanced Mode:</strong> ✅ Enabled</>}
                </div>
            )}
        </div>
    );
};

// Main GeneratorModal component
const GeneratorModal: React.FC<GeneratorModalProps> = ({ isOpen, onClose, selectedAgent, fetchTestCases, isGenerating, setIsGenerating, mode = 'scenarios', onDataRefresh }) => {

    const [activeTab, setActiveTab] = useState<GeneratorTab>(mode === 'scenarios' ? 'testcases' : 'documents');

    if (!isOpen) return null;

    const renderTabContent = () => {
        switch (activeTab) {
            case 'documents':
                return <DocumentsTab />;

            case 'requirements':
                return <RequirementsTab />;

            case 'testcases':
                return <TestCasesTab
                    selectedAgent={selectedAgent}
                    fetchTestCases={fetchTestCases}
                    isGenerating={isGenerating}
                    setIsGenerating={setIsGenerating}
                    onDataRefresh={onDataRefresh}
                />;

            case 'templates':
                return <TemplatesTab />;

            case 'execute':
                return (
                    <div className="tab-content">
                        <h3>Execute</h3>
                        <div style={{ padding: '2rem', color: '#666' }}>
                            <p>Natural language prompt-based execution using template definitions</p>
                            <div style={{ marginTop: '1rem' }}>
                                <label><strong>Enter your command:</strong></label>
                                <textarea
                                    placeholder="E.g., 'Generate business requirements from design doc using agile template with title, description, and priority fields' or 'Create test cases for MyAgent using functional template'"
                                    style={{
                                        width: '100%',
                                        height: '100px',
                                        padding: '0.75rem',
                                        margin: '0.5rem 0',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd'
                                    }}
                                />
                                <div style={{ marginTop: '1rem' }}>
                                    <h4>Example commands:</h4>
                                    <ul style={{ fontSize: '0.9rem', color: '#555' }}>
                                        <li>"Generate agile user stories from uploaded document with title, description, priority, and acceptance criteria"</li>
                                        <li>"Create business requirements using formal template with all required fields"</li>
                                        <li>"Generate test cases for agent testing with functional template"</li>
                                        <li>"Export current requirements as PDF with selected fields only"</li>
                                    </ul>
                                </div>
                                <button className="widget-button button-primary" style={{ marginTop: '1rem' }} disabled>
                                    Parse & Execute (Coming Soon)
                                </button>
                            </div>
                        </div>
                    </div>
                );

            default:
                return <div>Unknown tab</div>;
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}
                style={{ width: '95vw', maxWidth: '1400px', maxHeight: '90vh', overflow: 'auto' }}>

                {/* Header */}
                <div className="modal-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '1rem'
                }}>
                    <h2>Scenarios Generator & Export</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation" style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '0.5rem'
                }}>
                    {[
                        { id: 'documents', label: 'Documents' },
                        { id: 'requirements', label: 'Requirements' },
                        { id: 'testcases', label: 'Test Cases' },
                        { id: 'templates', label: 'Templates' },
                        { id: 'execute', label: 'Execute' }
                    ].map(tab => {
                        const isTabDisabled = mode === 'scenarios' ?
                            ['documents', 'requirements', 'templates', 'execute'].includes(tab.id) :
                            mode === 'documents' ?
                                ['testcases', 'templates', 'execute'].includes(tab.id) :
                                false;

                        const isTabVisible = mode === 'scenarios' ?
                            tab.id === 'testcases' :
                            true;

                        if (!isTabVisible) return null;

                        return (
                            <button
                                key={tab.id}
                                onClick={() => !isTabDisabled && setActiveTab(tab.id as GeneratorTab)}
                                disabled={isTabDisabled}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: activeTab === tab.id ? '#007bff' :
                                        isTabDisabled ? '#f5f5f5' : 'white',
                                    color: activeTab === tab.id ? 'white' :
                                        isTabDisabled ? '#999' : '#333',
                                    cursor: isTabDisabled ? 'not-allowed' : 'pointer',
                                    fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                                    transition: 'all 0.2s',
                                    opacity: isTabDisabled ? 0.6 : 1
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Tab Content */}
                {renderTabContent()}
            </div>
        </div>
    );
};

export default GeneratorModal;